import {
  RESTRICTION_MODEL_CONTEXTS,
  type RestrictionModelContextsType,
} from 'lumen/utils/constants/channel-pricing-constants';
import {
  MIN_LOS_NO_RESTRICTION_VALUE,
  MIN_LOS_RESTRICTION_TYPE,
  type MinLosRestrictionType,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type {
  MinimalRestrictionAction,
  RateGroupContextFields,
  RestrictionActionValue,
} from 'lumen/utils/constants/restriction-actions';

/* The context fields shared by restriction, restriction-recommendation and
   restriction-action models, so the rate-group predicates below can classify
   any of them (or a plain object) without depending on EmberData. */
type RestrictionContextFields = {
  context: RestrictionModelContextsType;
  rateGroupId: string | null;
};

export function isHotelLevel({
  context,
}: Pick<RestrictionContextFields, 'context'>): boolean {
  return context === RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL;
}

export function isRateGroupForId(
  { context, rateGroupId }: RestrictionContextFields,
  targetRateGroupId: string,
): boolean {
  return (
    context === RESTRICTION_MODEL_CONTEXTS.RATE_GROUP_LEVEL &&
    rateGroupId === targetRateGroupId
  );
}

/**
 * Builds the MinLOS restriction action values to send for a single restriction
 * context (hotel-level or a rate group such as Deep Discount).
 *
 * - override removed: re-send the recommended value, or nothing when absent.
 * - "No MinLOS restriction": send the no-restriction sentinel.
 * - otherwise: send the resolved value, or nothing when absent.
 */
export function minLosRestrictionValues(
  overrideRemoved: boolean,
  recommendationValue: number | null | undefined,
  restrictionType: MinLosRestrictionType,
  value: number | null,
): RestrictionActionValue[] | null {
  if (overrideRemoved) {
    return recommendationValue != null
      ? [{ type: RESTRICTION_TYPES.MIN_LOS, value: recommendationValue }]
      : null;
  }

  if (restrictionType === MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION) {
    return [
      { type: RESTRICTION_TYPES.MIN_LOS, value: MIN_LOS_NO_RESTRICTION_VALUE },
    ];
  }

  if (value == null) {
    return null;
  }

  return [{ type: RESTRICTION_TYPES.MIN_LOS, value }];
}

/* A MinLOS value of the no-restriction sentinel (0) and the NO_RESTRICTION type are
   two encodings of the same state; these convert between them. */
export function minLosValueToType(
  value: number | null | undefined,
): MinLosRestrictionType {
  return value === MIN_LOS_NO_RESTRICTION_VALUE
    ? MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
    : MIN_LOS_RESTRICTION_TYPE.MIN_LOS;
}

export function minLosTypeToValue(
  restrictionType: MinLosRestrictionType,
  minLos: number | null,
): number | null {
  return restrictionType === MIN_LOS_RESTRICTION_TYPE.NO_RESTRICTION
    ? MIN_LOS_NO_RESTRICTION_VALUE
    : minLos;
}

/* The fields that decide what a restriction accordion would send. `type` is omitted
   for overbooking (no MinLOS restriction type), and `value` is the sendable value —
   the no-restriction sentinel under "No MinLOS restriction". */
export interface RestrictionActionChangeInput {
  automationOn: boolean;
  type?: MinLosRestrictionType;
  value: number | null;
}

/**
 * True when an accordion's current sendable state differs from what is in place,
 * i.e. the user actually changed it. A plain field compare: an automation toggle, a
 * MinLOS type change, or a value edit each count as a change.
 *
 * Relies on the manual value/type being reset once automation is on (see
 * `resetHotelMinLosState` / `toggleOverbookingAutomation`), so an automated accordion
 * never reads as changed from a leftover edit.
 */
export function hasRestrictionActionChanged(
  now: RestrictionActionChangeInput,
  inPlace: RestrictionActionChangeInput,
): boolean {
  return (
    now.automationOn !== inPlace.automationOn ||
    now.type !== inPlace.type ||
    now.value !== inPlace.value
  );
}

type RestrictionActionConfigInput = RateGroupContextFields & {
  getRestrictionValues: () => RestrictionActionValue[] | null;
};

/**
 * Groups pending restriction configs into one `MinimalRestrictionAction` per
 * context: hotel-level values share a single config, while a rate group such as
 * Deep Discount (a distinct context) is emitted as its own config carrying
 * `rate_group_id`.
 *
 * Configs whose resolved values are empty are skipped. `getRestrictionValues` is
 * invoked here, so callers must call this only after any recommendation re-request
 * so the values read fresh data.
 */
export function mergeRestrictionActionsByContext(
  configs: RestrictionActionConfigInput[],
  fromDate: string,
): MinimalRestrictionAction[] {
  const merged: MinimalRestrictionAction[] = [];

  for (const config of configs) {
    const values = config.getRestrictionValues() ?? [];
    if (values.length === 0) {
      continue;
    }

    const existing = merged.find(
      (entry) =>
        entry.context === config.context &&
        entry.rate_group_id === (config.rateGroupId ?? null),
    );
    if (existing) {
      existing.values.push(...values);
    } else {
      merged.push({
        context: config.context,
        from_date: fromDate,
        values,
        rate_group_id: config.rateGroupId ?? null,
      });
    }
  }

  return merged;
}
